import { AddIcon } from '@navi/web-ui/lib/icons';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import { RootState } from '@cp/src/store';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState, useMemo } from 'react';
import MockRunsList from './MockRunsList';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { fetchMockRunsList } from '../../actions/FieldAllocationAction';
import {
  Tooltip as TooltipV2,
  TooltipContent,
  TooltipTrigger,
  Directions
} from '@cp/src/components/TooltipV2/TooltipV2';
import {
  BannerType,
  FIELD_ALLOCATION_KEY,
  MOCK_RUN_HISTORY_FETCH_TYPE,
  MockControlState
} from '../../types';
import InitiateMockModal from '../Modals/InitiateMockModal';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { SELF_SERVE_UI_CLICKSTREAM } from '@cp/src/service/clickStream.constant';
import { getServerTime } from '@cp/src/utils/DateHelper';
import MockBanner from './MockBanner';
import { getBannerType } from './utils';

const FieldAllocationsLeftPanel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParams = readQueryParams();

  const [mockModalState, setMockModalState] = useState({
    isVisible: false,
    currentStep: 1
  });
  const mockRunsListLoading = useSelector(
    (state: RootState) => state.fieldAllocation?.mockRunsListLoading
  );

  const mockRunCapacity = useSelector(
    (state: RootState) => state.fieldAllocation?.mockRunCapacity?.INITIATED
  );
  const mockInitiateState = useSelector(
    (state: RootState) => state.fieldAllocation?.mockControlsStatus?.initiateMock?.state
  );
  const mockInitiateMessage = useSelector(
    (state: RootState) => state.fieldAllocation?.mockControlsStatus?.initiateMock?.message
  );

  const monthEndDeadlineEpochMs = useSelector(
    (state: RootState) => state.fieldAllocation?.mockControlsStatus?.monthEndDeadlineEpochMs
  );

  const isMockInitiateAvailable = mockInitiateState !== MockControlState.UNAVAILABLE;

  const { allowed: isInitiateMockAllowed, message: mockRunCapacityExceededMessage = '' } =
    mockRunCapacity || {};

  const bannerType = getBannerType(mockInitiateState, mockInitiateMessage);
  const showBanner = bannerType !== BannerType.NONE;

  const secondsRemaining = useMemo(() => {
    if (!monthEndDeadlineEpochMs) return 0;
    const diff = monthEndDeadlineEpochMs - getServerTime();
    return Math.max(0, Math.floor(diff / 1000));
  }, [monthEndDeadlineEpochMs]);

  const handleOpenModal = useCallback(() => {
    addClickstreamEvent(SELF_SERVE_UI_CLICKSTREAM.LH_INITIATE_MOCK_CLICKED);
    setMockModalState({
      isVisible: true,
      currentStep: 1
    });
  }, []);

  useEffect(() => {
    const selectMockHandler = (mockId: string) => {
      const selectedMockId = queryParams[FIELD_ALLOCATION_KEY]?.selectedId;
      if (selectedMockId) return;
      const updatedTableParams = { ...queryParams };
      updatedTableParams[FIELD_ALLOCATION_KEY] = {
        ...queryParams[FIELD_ALLOCATION_KEY],
        selectedId: mockId
      };
      const url = createQueryParams(updatedTableParams);
      navigate(url, { replace: true });
    };

    dispatch(fetchMockRunsList(MOCK_RUN_HISTORY_FETCH_TYPE.CURRENT_MONTH, selectMockHandler));
  }, []);

  return (
    <>
      <div className={styles.leftPanel}>
        {showBanner && (
          <MockBanner
            bannerType={bannerType}
            message={mockInitiateMessage || ''}
            secondsRemaining={secondsRemaining}
          />
        )}
        <div className={styles.header}>
          <Typography variant="h3" color="var(--light-grayscale-content-2)">
            Mock runs
          </Typography>

          <Button
            variant="text"
            size="medium"
            startAdornment={<AddIcon color="var(--blue-base)" />}
            disabled={mockRunsListLoading || !isInitiateMockAllowed || !isMockInitiateAvailable}
            className="cursor-pointer"
            onClick={handleOpenModal}
          >
            {isInitiateMockAllowed && isMockInitiateAvailable ? (
              'Initiate mock'
            ) : (
              <TooltipV2 placement={Directions.TOP} hideStrategy="referenceHidden">
                <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                  <Typography variant="h4" color="var(--blue-base)">
                    Initiate mock
                  </Typography>
                </TooltipTrigger>
                <TooltipContent className="tooltipWrapper max-w-[240px] py-2">
                  {mockInitiateMessage || mockRunCapacityExceededMessage}
                </TooltipContent>
              </TooltipV2>
            )}
          </Button>
        </div>
        <div className={styles.mockRuns}>
          <MockRunsList />
        </div>
        <InitiateMockModal mockModalState={mockModalState} setMockModalState={setMockModalState} />
      </div>
    </>
  );
};

export default FieldAllocationsLeftPanel;
